import type { ReactNode } from 'react';
import { C } from '../../theme';
import type { Cercle, MembreFoyer } from '../../api/types';

/**
 * Le choix du cercle : « Pour qui ? » — Tout le foyer / Moi / Choisir…
 *
 * Reutilise a la validation d'une proposition ET au changement apres coup. Les
 * personnes proposees sont les membres du foyer qui ont un compte (les autres ne
 * peuvent rien voir), sans soi-meme : le createur voit toujours ses propres choses.
 */

export function libelleDuCercle(
  cercle: { visibility?: Cercle['visibility']; sharedWithUserIds?: string[] },
  membres: MembreFoyer[],
): string | null {
  if (cercle.visibility === 'PRIVATE') return 'Privé';
  if (cercle.visibility === 'CHOSEN') {
    const prenoms = (cercle.sharedWithUserIds ?? [])
      .map((userId) => membres.find((membre) => membre.userId === userId)?.displayName)
      .filter((nom): nom is string => nom !== undefined)
      .map((nom) => nom.split(' ')[0]);
    return prenoms.length === 0 ? 'Partagé' : `Partagé avec ${prenoms.join(', ')}`;
  }
  return null;
}

export function ChoixCercle({
  membres,
  utilisateurId,
  valeur,
  onChange,
}: {
  membres: MembreFoyer[];
  utilisateurId: string;
  valeur: Cercle;
  onChange: (cercle: Cercle) => void;
}) {
  const autres = membres.filter(
    (membre) => membre.userId !== null && membre.userId !== utilisateurId,
  );

  const basculerPersonne = (userId: string) => {
    const actuels = valeur.sharedWithUserIds;
    const suivants = actuels.includes(userId)
      ? actuels.filter((id) => id !== userId)
      : [...actuels, userId];
    onChange({ visibility: 'CHOSEN', sharedWithUserIds: suivants });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Pastille
          actif={valeur.visibility === 'HOUSEHOLD'}
          onClick={() => onChange({ visibility: 'HOUSEHOLD', sharedWithUserIds: [] })}
        >
          Tout le foyer
        </Pastille>
        <Pastille
          actif={valeur.visibility === 'PRIVATE'}
          onClick={() => onChange({ visibility: 'PRIVATE', sharedWithUserIds: [] })}
        >
          Moi seulement
        </Pastille>
        {/* Sans personne d'autre dans le foyer, « Choisir » ne commanderait rien. */}
        {autres.length > 0 ? (
          <Pastille
            actif={valeur.visibility === 'CHOSEN'}
            onClick={() => onChange({ visibility: 'CHOSEN', sharedWithUserIds: [] })}
          >
            Choisir…
          </Pastille>
        ) : null}
      </div>

      {valeur.visibility === 'CHOSEN' ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {autres.map((membre) => (
            <Pastille
              key={membre.id}
              actif={valeur.sharedWithUserIds.includes(membre.userId!)}
              onClick={() => basculerPersonne(membre.userId!)}
            >
              {membre.displayName.split(' ')[0]}
            </Pastille>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Pastille({
  actif,
  onClick,
  children,
}: {
  actif: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={actif}
      onClick={onClick}
      style={{
        border: `2px solid ${actif ? C.violet : C.bordure}`,
        background: actif ? C.violetFond : C.carte,
        color: actif ? C.violetTexte : C.secondaire,
        borderRadius: 999,
        padding: '6px 12px',
        fontSize: 13,
        fontWeight: 800,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
